import { RuleContext } from '../rules/client-rule-context';
import { Injectable, OnDestroy } from '@angular/core';
import { ChangeSourceType, DocumentChangedInfo, SharedConstants, User } from '@formbird/types';
import { UtilConflict, UtilRules, UtilSequenceProcess, UtilValidation } from '@formbird/shared';
import { cloneDeep, includes, isEqual } from 'lodash';
import { NotificationService } from '../notification/notification.service';
import { RuleEngineService } from '../rules/rule-engine.service';
import { ModifiedFieldService } from '../document/modified-field.service';
import { TabService } from '../tab/tab.service';
import { PreProcessorFieldService } from '../preprocessor/pre-processor-field.service';
import { Observable, Subject, Subscription } from 'rxjs';
import { concatMap, debounceTime } from 'rxjs/operators';
import { DocumentService } from '../document/document.service';
import { UnsavedDocumentService } from '../document/unsaved-document.service';
import { LoggedInUserService } from '../user/logged-in-user.service';
import { CurrentDocumentService } from '../document/current-document.service';
import { ConfigService } from '../config/config.service';
import { BroadcastService } from '../broadcast/broadcast.service';
import { ClientAccessService } from '../access/client-access.service';
import { ClientConstants } from '../../constants/ClientConstants';
import { IApplicationState } from '../../redux/state/application.state';
import { select } from '../../redux/decorators/select';
import { AppStore } from '../../redux/store/app.store';
import { formDocumentUpdated } from '../../redux/actions';

const logger = console;

export interface RulesData {
  eventName: string;
  document: any;
  documentContext: any;
  callback?: any;
  error?: any;
  results?: any;
}

@Injectable({
  providedIn: 'root'
})
export class ValidationService implements OnDestroy {
  @select(['formState', 'documentChangedInfo']) documentChangedInfo$: Observable<DocumentChangedInfo>;
  @select(['userState', 'user']) user$: Observable<User>;

  private subs: Subscription = new Subscription();

  documentData: any;
  user: User;

  /***------------------- Exported data -------------------------***/
  public validationCtrlValues = {};

  /***------------------- Local data ----------------------------***/
  public eventList = {};

  private pendingSave = null;

  private queue = new Subject<RulesData>();

  constructor(
    private documentService: DocumentService,
    private unsavedDocumentService: UnsavedDocumentService,
    private notificationService: NotificationService,
    private modifiedFieldService: ModifiedFieldService,
    private ruleEngineService: RuleEngineService,
    private loggedInUserService: LoggedInUserService,
    private currentDocumentService: CurrentDocumentService,
    private preProcessorFieldService: PreProcessorFieldService,
    private configService: ConfigService,
    private broadcastService: BroadcastService,
    private tabService: TabService,
    private clientAccessService: ClientAccessService,
    private appStore: AppStore<IApplicationState>
  ) {

    const subDocData = this.currentDocumentService.documentData$.subscribe(data => {
      this.documentData = data;
    });

    const subUser = this.user$.subscribe((user: User) => this.user = user);

    const documentChangedSub = this.documentChangedInfo$.
      pipe(debounceTime(ClientConstants.VALIDATION_INTERVAL_TIME)).subscribe((documentChangedData: DocumentChangedInfo) => {
        this.handleOnFieldChange(documentChangedData);
      });

    const results = this.queue.pipe(
      concatMap((rulesData: RulesData) => {
        return this.executeDocumentEventPromise(rulesData);
      }));
    const ruleSub = results.subscribe((data: RulesData) => {
      data.callback(data.error, data.results);
    });

    this.subs.add(subDocData);
    this.subs.add(subUser);
    this.subs.add(documentChangedSub);
    this.subs.add(ruleSub);
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  /***------------------- Exported functions --------------------***/
  loadValidators(tplItem, fieldName) {

    const validators = [];

    const required = {
      'name': ClientConstants.VALIDATION_REQUIRED_KEY,
      'text': ClientConstants.VALIDATION_REQUIRED_MESSAGE
    };
    validators.push(required);

    const minLength = {
      'name': ClientConstants.VALIDATION_MIN_LENGTH_KEY,
      'text': ClientConstants.VALIDATION_MIN_LENGTH_MESSAGE.replace(/%s/g, tplItem.minLength)
    };
    validators.push(minLength);

    const maxLength = {
      'name': ClientConstants.VALIDATION_MAX_LENGTH_KEY,
      'text': ClientConstants.VALIDATION_MAX_LENGTH_MESSAGE.replace(/%s/g, tplItem.maxLength)
    };
    validators.push(maxLength);

    return validators;
  }

  waitPendingEvents(save) {

    logger.info('ModifiedFieldService.formDirty.validating ' + this.modifiedFieldService.formDirty.validating);

    if (this.modifiedFieldService.formDirty.validating) {

      this.modifiedFieldService.setShowSpinner(true);
      this.modifiedFieldService.setDirty(false);

      this.pendingSave = save;

    } else {
      save();
    }
  }

  private updateDocumentToStore(prevDocument, document, ruleContext) {
    const diffs = UtilConflict.getDiffs(prevDocument, document);

    if (diffs && diffs.length) {
      const documentChangedInfo: DocumentChangedInfo = {
        documentId: document.documentId,
        fieldName: ruleContext.fieldChanged,
        newValue: diffs,
        sourceType: ChangeSourceType.COMPONENT
      };
      this.dispatchStoreAction(formDocumentUpdated(documentChangedInfo));
    }
  }

  private handleOnFieldChange(documentChangedData: DocumentChangedInfo) {

    if (documentChangedData) {
      const state = this.appStore.getState().formState;
      const documentId = documentChangedData.documentId;
      const document = cloneDeep(state.documents[documentId]);
      const formParams = documentChangedData.formParameters;
      const templateId = formParams.overrideTemplateId ? formParams.overrideTemplateId : document.systemHeader.templateId;
      const template = state.documents[templateId];
      const fieldName = documentChangedData.fieldName;
      const formParameters = documentChangedData.formParameters;
      const component = template.components.filter(comp => comp.name === fieldName)[0];

      if (documentChangedData.shouldCompareValues !== false) {

        if (isEqual(documentChangedData.newValue, documentChangedData.oldValue) ||
          (!UtilValidation.hasValue(documentChangedData.oldValue) &&
            !UtilValidation.hasValue(documentChangedData.newValue))) { // first run.
          return;
        }

        const isChanged = this.modifiedFieldService.isChangedField(documentId, fieldName, formParameters.unsavedDocumentListId);
        if (!isChanged && isEqual(documentChangedData.newValue, documentChangedData.oldValue)) {
          return;
        }
      }

      if (!component.disableSave) {
        this.modifiedFieldService.addModifiedField(documentId, fieldName, formParameters.unsavedDocumentListId);
      }

      this.preProcessorFieldService.preProcessOneField(component, document);

      const orgValue = this.appStore.getState().formState.orgDocuments[documentId][fieldName];
      this.validateChangedField(component, document, template, orgValue,
        documentChangedData.newValue, documentChangedData.oldValue, formParameters);
    }
  }

  executeOnFieldChangeWithNoChanges(documentChangedInfo: DocumentChangedInfo) {
    this.handleOnFieldChange(documentChangedInfo);
  }

  private executeOnFieldChange(document, docContext) {

    const _self = this;

    const currentValidating = new Date();
    _self.modifiedFieldService.formDirty.validating = currentValidating;

    _self.executeDocumentEvent(SharedConstants.RULESET_RELATIONSHIPS.ON_FIELD_CHANGE_EVENT_NAME.name, document, docContext,
      function (error, result) {

        if (result) {

          _self.broadcastValidationEvent();

        }

        if (_self.modifiedFieldService.formDirty.validating === currentValidating) {
          if (_self.pendingSave !== null) {
            _self.pendingSave();
          }

          _self.modifiedFieldService.formDirty.validating = null;
          _self.pendingSave = null;
        }
      });
  }

  private validateChangedField(component, document, template, originValue, newValue, oldValue, formParameters) {

    const fieldName = component.name;

    this.initFieldValidator(document, template, component, fieldName, newValue);

    const docContext = {} as any;
    docContext.fieldChanged = fieldName;
    docContext.originValue = originValue;
    docContext.newValue = newValue;
    docContext.oldValue = oldValue;
    docContext.templateId = template.documentId;
    docContext.formParameters = formParameters;
    docContext.template = template;
    docContext.databaseMode = this.configService.clientConfig().defaultSearchProvider;

    this.executeOnFieldChange(document, docContext);
  }

  validateChangedFieldValues(scope, originValue, newValue, oldValue) {

    const tplField = scope.tplItem;
    const fieldName = scope.fieldName;

    const document = scope.doc;
    const template = scope.tpl;

    this.initFieldValidator(document, template, tplField, fieldName, newValue);

    const docContext = {} as any;
    docContext.fieldChanged = fieldName;
    docContext.originValue = originValue;
    docContext.newValue = newValue;
    docContext.oldValue = oldValue;
    docContext.templateId = scope.tplId;
    docContext.formParameters = scope.formParameters;
    docContext.template = scope.tpl;
    docContext.databaseMode = this.configService.clientConfig().defaultSearchProvider;


    this.executeOnFieldChange(scope.doc, docContext);
  }

  onLoadDocument(scope, document, docContext, callback) {

    const template = docContext.template;

    // reset validation values due to template/document OnLoad
    this.validationCtrlValues[document.documentId] = {
      templateId: template.documentId
    };

    docContext.formParameters = scope.formParameters;

    this.executeDocumentEvent(SharedConstants.RULESET_RELATIONSHIPS.ONLOAD_EVENT_NAME.name, document, docContext, callback);
  }

  onPreSaveDocument(formParameters, document, docContext, callback) {

    docContext.formParameters = formParameters;

    if (!docContext.formParameters) {

      const unsavedDocumentListId = docContext.unsavedDocumentListId;
      const unsavedDocs = this.unsavedDocumentService.getDocuments();

      if (unsavedDocs && unsavedDocs[unsavedDocumentListId]) {

        docContext.formParameters = {};
        docContext.formParameters.hierarchyDocInfo = unsavedDocs[unsavedDocumentListId].hierarchyDocInfo;

      }
    }

    this.executeDocumentEvent(SharedConstants.RULESET_RELATIONSHIPS.PRESAVE_EVENT_NAME.name, document, docContext, callback);
  }

  broadcastValidationEvent() {
    this.broadcastEvent(SharedConstants.EVENT_VALIDATION_VALUES_NAME, this.validationCtrlValues);
  }

  broadcastEvent(eventName, eventValue?) {
    this.broadcastService.broadcast(eventName, eventValue);
  }

  dispatchStoreAction(action) {
    this.appStore.dispatch(action);
  }

  /***---------------------- Helper functions -----------------***/
  private initFieldValidator(document, template, tplField, fieldName, fieldValue) {

    this.initValidationValue(document, template, fieldName);

    if (tplField.mandatory === true) {
      this.setValidationValue(document, fieldName, 'required', !UtilValidation.hasValue(fieldValue));
    }

    if (tplField.minLength) {
      this.setValidationValue(document, fieldName, 'hasMinLength',
        !UtilValidation.validateMinLength(fieldValue, tplField.minLength));
    }

    if (tplField.maxLength) {
      this.setValidationValue(document, fieldName, 'hasMaxLength',
        !UtilValidation.validateMaxLength(fieldValue, tplField.maxLength));
    }
  }

  async executeRuleSet(eventName, processDoc, documentContext, eventRule) {

    const _self = this;

    return new Promise((resolve, reject) => {

      documentContext.eventName = eventName;

      const ruleContext = new RuleContext(_self, eventName, processDoc, documentContext);

      _self.ruleEngineService.processRuleFlow(processDoc, documentContext, eventRule, ruleContext, function (result) {

        const msg = result ? result.message : null;
        const status = result ? result.status : SharedConstants.STATUS_SUCCESS;
        if (msg) {
          _self.notificationService.printMessage(msg, status, null, 'toast-top-right', false);
        }

        if (status && status.toLowerCase() === SharedConstants.STATUS_ERROR) {

          reject(result);

        } else {

          switch (eventName) {

            case SharedConstants.RULESET_RELATIONSHIPS.ONLOAD_EVENT_NAME.name:
              _self.broadcastEvent(SharedConstants.ONLOAD_DONE_RULES_EVENT_NAME, true);
              break;

          }

          resolve(result);
        }
      });

    });

  }

  executeDocumentEventPromise(rulesData: RulesData) {
    const _self = this;

    const eventName = rulesData.eventName;
    const document = rulesData.document;
    const documentContext = rulesData.documentContext;

    logger.info('%c Process ' + eventName + ' rule for document: ' + document.documentId, `background: #337af0;color:red`);

    return new Promise((resolve) => {
      if (SharedConstants.RULESET_RELATIONSHIPS.ON_FIELD_CHANGE_EVENT_NAME.name === eventName &&
        !this.isExistsChangedFieldInRulesetTriggeredOn(eventName, documentContext)) {
        resolve(rulesData);
        return;
      }

      _self.loadRule(eventName, documentContext.template).then(
        function successFunc(rulesets: []) {

          if (!rulesets.length) {
            resolve(rulesData);
            return;
          }

          function processEvent(user, accountControl) {

            documentContext.user = user ? user : {};
            documentContext.accountControl = accountControl;

            const processDoc = document ? document : {};

            _self.setParentTplDoc(documentContext, processDoc);
            _self.setChildTplDoc(documentContext, processDoc);

            function asyncJob(ruleset) {
              if (ruleset) {

                return _self.executeRuleSet(eventName, processDoc, documentContext, ruleset);

              }
            }

            const prevDocument = cloneDeep(document);

            UtilSequenceProcess.processList(rulesets, asyncJob).then(
              function allPromiseSuccessFunc(results) {

                if (eventName === SharedConstants.RULESET_RELATIONSHIPS.ON_FIELD_CHANGE_EVENT_NAME.name ||
                eventName === SharedConstants.RULESET_RELATIONSHIPS.ONLOAD_EVENT_NAME.name) {
                  _self.updateDocumentToStore(prevDocument, document, documentContext);
                }

                rulesData.results = results;
                resolve(rulesData);

              },
              function errorFunc(err) {

                rulesData.error = err;
                resolve(rulesData);

                if (eventName === SharedConstants.RULESET_RELATIONSHIPS.PRESAVE_EVENT_NAME.name) {

                  _self.broadcastEvent(ClientConstants.PRESAVE_DOCUMENT_FORM_RESET, {
                    document: processDoc,
                    unsavedDocumentListId: documentContext.unsavedDocumentListId
                  });
                }

              }
            );
          }

          _self.loggedInUserService.loadLoggedUser().then(
            function (result: any) {

              processEvent(result.user, _self.user.accountControlDocument);

            },
            function (err) {

              let msg = 'Error loading user data: ';
              msg += err.message ? err.message : err.statusText ? err.statusText : ' Status code: ' + err.status;

              logger.error(msg);

              rulesData.error = new Error(msg);
              resolve(rulesData);
            });
        },
        function errorFunc(err) {

          logger.error('Load rule error: ' + err);
          rulesData.error = err;
          resolve(rulesData);

        }
      );
    });

  }

  executeDocumentEvent(eventName, document, documentContext, callback) {

    const rulesData: RulesData = {
      eventName,
      document,
      documentContext,
      callback
    };

    this.queue.next(rulesData);
  }

  loadParentTplDocFromUnsavedList(unsavedListItems, hierachyDoc) {

    const result = {
      parentDoc: null,
      parentTpl: null
    };


    for (let i = 0; i < unsavedListItems.length; i++) {

      const parentId = hierachyDoc.parentDocumentId;

      result.parentDoc = this.unsavedDocumentService.getDocumentOnly(parentId);

      if (result.parentDoc) {

        if (this.documentData && this.documentData.overrideTemplateId) {

          result.parentTpl = this.unsavedDocumentService.getTemplateOnly(this.documentData.overrideTemplateId);

        } else {

          result.parentTpl = this.unsavedDocumentService.getTemplateOnly(result.parentDoc.systemHeader.templateId);

        }

        break;
      }
    }

    return result;
  }

  private setParentTplDoc(documentContext, document) {

    let result = {} as any;

    const hasHierarchyDoc = documentContext.formParameters && documentContext.formParameters.hierarchyDocInfo;
    if (hasHierarchyDoc) {

      const hierachyDoc = documentContext.formParameters.hierarchyDocInfo;

      if (hierachyDoc.documentId === document.documentId ||
        documentContext.formParameters.selectedDocumentId === document.systemHeader.templateId) {

        documentContext.isChildDoc = true;

        // find the parent in the list of child document hierachy first (ie: ChildDocumentList)
        const unsavedChildListItems = this.unsavedDocumentService.getDocumentListByType(ClientConstants.CHILD_DOCUMENT_INFO_LIST);

        result = this.loadParentTplDocFromUnsavedList(unsavedChildListItems, hierachyDoc);

        if (!result.parentDoc) {

          // if parent does not exist in the child document list, find it in the main document list
          const unsavedMainListItems = this.unsavedDocumentService.getDocumentListByType(ClientConstants.MAIN_DOCUMENT_INFO_LIST);

          result = this.loadParentTplDocFromUnsavedList(unsavedMainListItems, hierachyDoc);
        }
      }
    }

    documentContext.parentDocument = result.parentDoc;
    documentContext.parentTemplate = result.parentTpl;
  }

  private setChildTplDoc(documentContext, document) {

    const documents = this.appStore.getState().formState.documents;

    let childDoc;
    let childTpl;

    // find in ChildDocumentList that this one is the parent
    const unsavedChildListItems =
      this.unsavedDocumentService.getDocumentListByType(ClientConstants.CHILD_DOCUMENT_INFO_LIST);

    if (unsavedChildListItems && unsavedChildListItems.length) {

      for (let i = 0; i < unsavedChildListItems.length; i++) {

        const hierarchyDocInfo = unsavedChildListItems[i].hierarchyDocInfo;

        if (hierarchyDocInfo.parentDocumentId === document.documentId) {

          const childDocId = hierarchyDocInfo.documentId;

          childDoc = documents[childDocId];

          if (childDoc) {

            childTpl = documents[childDoc.systemHeader.templateId];

            break;

          }
        }
      }
    }

    documentContext.childDocument = childDoc;
    documentContext.childTemplate = childTpl;
  }

  private loadRulesetById(docId, ruleName) {

    const _self = this;

    return new Promise((resolve, reject) => {

      _self.documentService.getDocumentOnly(docId).then(
        function successFunc(doc) {

          resolve(doc.ruleSetDSL);

        },
        function errorFunc(err) {

          let msg = ruleName + ' ruleset: ' + docId + ' not found due to error: ';
          msg += err && err.message ? err.message : err;

          if (ruleName === SharedConstants.RULESET_RELATIONSHIPS.ONLOAD_EVENT_NAME.name) {

            reject(new Error(msg));

          } else {

            _self.notificationService.printMessage(msg, SharedConstants.STATUS_WARNING, null, { x: 'right', y: 'top' });

            // do not break the chain in order to load the next ruleset document
            resolve();
          }

        }
      );

    });

  }

  private loadRule(ruleName, template) {

    const _self = this;

    return new Promise((resolve, reject) => {

      if (template) {

        const relName = UtilRules.getRelatedName(ruleName);

        const rulesetRelValues = template[relName];
        if (rulesetRelValues) {

          logger.info('Using number of related rulesets: ' + rulesetRelValues.length);

          const promises = [];

          rulesetRelValues.forEach(function (obj) {
            promises.push(_self.loadRulesetById(obj.documentId, ruleName));
          });

          Promise.all(promises).then(
            function successFunc(ruleFlows) {

              logger.info('Found related rulesets: ' + ruleFlows.length);

              resolve(ruleFlows);

            },
            function errorFunc(err) {

              logger.warn('Not found related rulesets.');

              reject(err);

            }
          );

        } else {

          const rules = template.rules;
          const foundRuleset = [];

          if (rules && rules.length) {
            logger.info('Using embeded ruleset in template...');

            for (let rId = 0; rId < rules.length; rId++) {

              if (rules[rId].name === ruleName) {
                logger.warn('DEPRECATED: please use the ruleset related document structure ' +
                  'in the rules definition instead of having the ruleFlow script directly ' +
                  'in template: ' + template.documentId);

                foundRuleset.push(rules[rId].ruleFlow);

              }

            }
          }

          resolve(foundRuleset);
        }
      }

    });
  }

  initValidationValue(document, template, field) {

    const affectedDocId = document.documentId;

    if (!this.validationCtrlValues[affectedDocId]) {

      this.validationCtrlValues[affectedDocId] = {
        templateId: template.documentId
      };

    }

    if (!this.validationCtrlValues[affectedDocId][field]) {

      this.validationCtrlValues[affectedDocId][field] = {};

    }
  }

  private setValidationValue(affectedDoc, field, name, flag) {

    if (flag !== undefined) {

      // use string values of "yes" and "true" so that they can be used inside the rules.
      const result = (flag === 'yes' || flag === 'true' || flag === true);

      if (this.validationCtrlValues[affectedDoc.documentId] &&
        this.validationCtrlValues[affectedDoc.documentId][field]) {

        if (this.validationCtrlValues[affectedDoc.documentId][field][name] !== result) {
          this.validationCtrlValues[affectedDoc.documentId][field][name] = result;

          this.validateFormFields({
            documentId: affectedDoc.documentId,
            fieldName: field,
            validateName: name
          });
        }

      }
    }
  }

  private isExistsChangedFieldInRulesetTriggeredOn(eventName, documentContext) {
    let isChangedFieldFound = false;
    if (SharedConstants.RULESET_RELATIONSHIPS.ON_FIELD_CHANGE_EVENT_NAME.name === eventName) {
      const rulesetRelValues = documentContext.template[SharedConstants.RULESET_RELATIONSHIPS.ON_FIELD_CHANGE_EVENT_NAME.relName]
      if (rulesetRelValues && rulesetRelValues.length > 0) {
        for (let i = 0; i < rulesetRelValues.length; i++) {
          const ruleObj = rulesetRelValues[i];
          isChangedFieldFound = !ruleObj.triggeredOn || includes(ruleObj.triggeredOn, documentContext.fieldChanged);
          if (isChangedFieldFound) {
            break;
          }
        }
      }
    }
    return isChangedFieldFound;
  }

  /***------------------- End Rule context -------------------****/

  validateFormFields = (value?: any) => {

    this.broadcastEvent(ClientConstants.VALIDATE_FORM_FIELDS, value);

  }
}
